<template>
  <div>
    <div
      class="sectionTitle package"
      data-automation="package-title"
    >
      {{ selectedPackage.name }} <span
        class="tagline"
        data-automation="package-tagline"
      >{{ selectedPackage.title }}</span>
    </div>

    <div
      v-if="selectedPackage.readme"
      class="menu showOnMobile"
    >
      <div class="menuItems">
        <a
          class="menuItem"
          :class="{ active: currentTab === 'overview' }"
          @click="setCurrentTab('overview')"
        >Overview</a>
        <a
          data-automation="package-readme-tab"
          class="menuItem"
          :class="{ active: currentTab === 'readme' }"
          @click="setCurrentTab('readme')"
        >Readme</a>
      </div>
    </div>

    <div
      v-if="currentTab === 'readme'"
      class="readme"
      data-automation="package-readme"
    >
      <pre v-if="readme.raw">
        {{ readme.raw }}
      </pre>
      <!-- eslint-disable vue/no-v-html -->
      <!-- We sanitize this below, so it should be safe with v-html -->
      <div
        v-if="readme.html"
        v-html="readme.html"
      />
      <!-- eslint-enable vue/no-v-html -->
    </div>

    <div v-if="currentTab === 'overview'">
      <div
        v-if="selectedPackage.tran_archive"
        class="infoBlock notice"
        data-automation="package-notice"
      >
        <div>This package has been archived and is no longer available for normal use. Any archived versions can be accessed below.</div>
      </div>

      <InfoBlockTextWithLinks
        label=""
        data-automation="package-description"
        :value="selectedPackage.description"
      />

      <div
        v-if="!selectedPackage.tran_archive"
        class="infoBlock"
      >
        <div class="textWithButton">
          <div
            class="label"
            data-automation="package-install-code"
          >
            Install Code
          </div>
          <button
            class="copyButton"
            data-clipboard-target="#installPackage"
          >
            Copy
          </button>
        </div>
        <pre><code
          id="installPackage"
          data-automation="package-install-package"
        >{{ installCode }}</code></pre>
      </div>

      <InfoBlockText
        label="Version"
        data-automation="package-version"
        :value="selectedPackage.version"
      />

      <InfoBlockText
        v-if="display_ash"
        label="ASH"
        data-automation="ash-value"
        :value="selectedPackage.ash"
      />

      <InfoBlockTextWithLinks
        label="About"
        data-automation="package-about"
        :value="selectedPackage.url"
      />

      <InfoBlockPackageList
        label="Imports"
        data-automation="package-imports"
        :value="selectedPackage.getDependencies(0, false)"
      />

      <InfoBlockPackageList
        label="Depends"
        data-automation="package-depends"
        :value="selectedPackage.getDependencies(1, false)"
      />

      <InfoBlockPackageList
        label="Suggests"
        data-automation="package-suggests"
        :value="selectedPackage.getDependencies(2, false)"
      />

      <InfoBlockPackageList
        label="Linking To"
        data-automation="package-linking-to"
        :value="selectedPackage.getDependencies(3, false)"
      />

      <InfoBlockPackageList
        label="Reverse Imports"
        data-automation="package-reverse-imports"
        :value="selectedPackage.getDependencies(0, true)"
      />

      <InfoBlockPackageList
        label="Reverse Depends"
        data-automation="package-reverse-depends"
        :value="selectedPackage.getDependencies(1, true)"
      />

      <InfoBlockPackageList
        label="Reverse Suggests"
        data-automation="package-reverse-suggests"
        :value="selectedPackage.getDependencies(2, true)"
      />

      <InfoBlockPackageList
        label="Reverse Linking To"
        data-automation="package-reverse-linking-to"
        :value="selectedPackage.getDependencies(3, true)"
      />

      <InfoBlockText
        label="Published"
        data-automation="package-published"
        :value="publishedFormatted"
      />

      <InfoBlockText
        label="Available as of"
        data-automation="package-available-asof"
        :value="availableFormatted"
      />

      <InfoBlockDownloads
        v-if="metricsEnabled"
        label="Monthly Downloads"
        data-automation="package-monthly-downloads"
        :value="selectedPackage.downloads"
      />

      <InfoBlockText
        label="Author"
        data-automation="package-author"
        :value="selectedPackage.author"
      />

      <InfoBlockText
        label="Maintainer"
        data-automation="package-maintainer"
        :value="selectedPackage.maintainer"
      />

      <InfoBlockTextWithLinks
        label="Bug Reports"
        data-automation="package-bug-reports"
        :value="selectedPackage.bug_reports"
      />

      <InfoBlockText
        label="License"
        data-automation="package-license"
        :value="selectedPackage.license"
      />

      <InfoBlockText
        v-if="currentRepoSources.length > 1"
        label="Source"
        data-automation="package-source"
        :value="sourceName"
      />

      <InfoBlockText
        label="Needs Compilation"
        data-automation="package-compilation"
        :value="selectedPackage.needs_compilation"
      />

      <InfoBlockText
        label="Size"
        data-automation="package-size"
        :value="selectedPackage.getSize()"
      />

      <InfoBlockText
        label="Remote SHA"
        data-automation="package-remote-sha"
        :value="selectedPackage.remote_sha"
      />

      <InfoBlockText
        label="System Requirements"
        data-automation="package-sysreqs"
        :value="selectedPackage.system_requirements"
      />

      <PackageSystemDependencies
        v-if="selectedOS.sysReqs && !selectedPackage.tran_archive"
        :repo-id="currentRepo.id"
        :selected-package="selectedPackage"
      />

      <InfoBlockText
        label="Reverse Enhances"
        :value="selectedPackage.reverse_enhances"
      />

      <InfoBlockTextWithLinks
        label="Source File"
        data-automation="package-source-file"
        :value="downloadLink"
      />

      <div
        v-if="selectedPackage.archived && selectedPackage.archived.length"
        class="infoBlock"
      >
        <div
          class="label"
          data-automation="package-archived-versions"
        >
          Archived Versions
        </div>
        <div class="flex">
          <div>
            <ArchivesList :archives="selectedPackage.archived" />
          </div>
          <div
            v-if="selectedArchive"
            class="archiveItemInfo"
            data-automation="package-archive-item-info"
            :style="{marginTop: (selectedArchiveIndex * 61) + 1 + 'px'}"
          >
            <div class="infoBlock">
              <div class="textWithButton">
                <div class="label">
                  Install Code
                </div>
                <button
                  class="copyButton"
                  data-clipboard-target="#installVersion"
                >
                  Copy
                </button>
              </div>
              <pre><code id="installVersion">{{ installArchivedCode }}</code></pre>
            </div>

            <InfoBlockTextWithLinks
              v-if="selectedArchive"
              label="Source File"
              data-automation="package-source-file"
              :value="selectedArchiveDownloadLink"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import { mapState, mapGetters } from 'vuex'
import InfoBlockText from '../InfoBlockText'
import InfoBlockPackageList from '../InfoBlockPackageList'
import InfoBlockTextWithLinks from '../InfoBlockTextWithLinks'
import InfoBlockDownloads from '../InfoBlockDownloads'
import ArchivesList from '../ArchivesList'
import PackageSystemDependencies from '../PackageSystemDependencies'
import { serverPath, helpers } from '../../api/http-common'
import { packageUtils } from '../../utils/package-utils'
import { rewriteGithubBlobImageURIs, uriToAbs } from '../../utils/path-utils'
import filesize from 'filesize'
import marked from 'marked'
import sanitizeHtml from 'sanitize-html'
import { REPO_TYPE_BIOCONDUCTOR } from '../../constants/repo-types'
import { parseTimeToString } from '../../utils/time'
import { isCranSource } from '../../utils/repos'

function removeDuplicates (myArr, prop) {
  return myArr.filter((obj, pos, arr) => {
    return arr.map(mapObj => mapObj[prop]).indexOf(obj[prop]) === pos
  })
}

export default {
  name: 'Package',
  components: {
    InfoBlockText,
    InfoBlockPackageList,
    InfoBlockTextWithLinks,
    InfoBlockDownloads,
    ArchivesList,
    PackageSystemDependencies,
  },
  data: function () {
    return {
      currentTab: 'overview',
    }
  },
  computed: {
    ...mapState({
      selectedPackage: state => ({
        ...state.packages.selectedPackage,
        getDependencies: (type, reverse) => {
          const links = state.packages.selectedPackage.links
          if (Array.isArray(links)) {
            return removeDuplicates(
              links.filter(i => (i.type === type && !!i.reverse === reverse)),
              'name')
          } else {
            return []
          }
        },
        getSize: () => {
          const size = state.packages.selectedPackage.package_size
          return (size) ? filesize(size) : ''
        },
        // Compute the base URL against which all relative references should be resolved.
        assetsPath: `${helpers.basePath(window)}__api__/packages/${state.packages.selectedPackage.id}/files/`,
      }),
      selectedArchive: state => state.packages.selectedArchive,
      currentRepoSources: state => state.repos.currentRepoSources,
      display_ash: state => state.status.display_ash,
      currentRepoType: state => state.repos.currentRepoType,
      selectedOS: state => state.packages.selectedOS,
      metricsEnabled: state => state.status.metricsEnabled,
    }),
    ...mapGetters([
      'currentRepo',
      'getDocument',
    ]),
    repoName: function () {
      return this.currentRepo.name
    },
    installCode: function () {
      // If binary packages are unsupported (e.g., on macOS), set the type
      // to 'source' to suppress the confusing source installation prompt when
      // running in an interactive R session. Otherwise, omit 'type' to default
      // to binary installation on all OSs.
      let installArgs = ''
      if (!this.selectedOS.binaries) {
        installArgs = ", type = 'source'"
      }

      const name = this.selectedPackage.name
      if (this.currentRepoType === REPO_TYPE_BIOCONDUCTOR) {
        return `BiocManager::install('${name}'${installArgs})`
      }
      return `install.packages('${name}'${installArgs})`
    },
    installArchivedCode: function () {
      const name = this.selectedPackage.name
      const version = this.selectedArchive.version
      if (this.currentRepoType === REPO_TYPE_BIOCONDUCTOR) {
        return `devtools::install_version('${name}', '${version}', repos = BiocManager::repositories())`
      }
      return `devtools::install_version('${name}', '${version}')`
    },
    showLocalTZ: function () {
      return !isCranSource(this.currentRepoSources)
    },
    publishedFormatted: function () {
      return packageUtils.getPackageDate(this.selectedPackage, this.showLocalTZ)
    },
    availableFormatted: function () {
      if (this.selectedPackage.occurred) {
        return parseTimeToString(this.selectedPackage.occurred, this.showLocalTZ)
      }
      return ''
    },
    biocPath: function () {
      if (this.currentRepoSources) {
        const src = this.currentRepoSources.find(item => item.id === this.selectedPackage.source_id)
        if (src.name) {
          return src.name.split('-').reduce((acc, part, idx) => {
            if (idx > 1) {
              return `${acc}/${part}`
            } else {
              return part
            }
          })
        }
      }
      return ''
    },
    downloadLink: function () {
      if (!this.selectedPackage.tran_archive) {
        if (this.currentRepoType === REPO_TYPE_BIOCONDUCTOR) {
          return `${serverPath()}${this.repoName}/packages/${this.biocPath}/src/contrib/${this.selectedPackage.name}_${this.selectedPackage.version}.tar.gz`
        } else {
          let downloadPath = 'latest/src/contrib/'
          if (this.selectedPackage.path) {
            downloadPath += this.selectedPackage.path + '/'
          }
          return `${serverPath()}${this.repoName}/${downloadPath}${this.selectedPackage.name}_${this.selectedPackage.version}.tar.gz`
        }
      }
      return ''
    },
    selectedArchiveDownloadLink: function () {
      if (this.selectedArchive) {
        if (this.currentRepoType === REPO_TYPE_BIOCONDUCTOR) {
          return `${serverPath()}${this.repoName}/packages/${this.biocPath}/src/contrib/Archive/${this.selectedPackage.name}/${this.selectedPackage.name}_${this.selectedArchive.version}.tar.gz`
        } else {
          return `${serverPath()}${this.repoName}/latest/src/contrib/Archive/${this.selectedPackage.name}/${this.selectedPackage.name}_${this.selectedArchive.version}.tar.gz`
        }
      } else {
        return ''
      }
    },
    selectedArchiveIndex: function () {
      if (this.selectedArchive) {
        return this.selectedPackage.archived.findIndex((item) => item.version === this.selectedArchive.version)
      }
      return 0
    },
    sourceName: function () {
      const source = this.currentRepoSources.find(s => { return s.id === this.selectedPackage.source_id })
      if (source) {
        return source.name
      }
      return ''
    },
    readme: function () {
      if (this.selectedPackage.readmeFile) {
        if (!/\.md$/i.test(this.selectedPackage.readme)) {
          // The file doesn't have a markdown extension, just render as plain text.
          return { raw: this.selectedPackage.readmeFile }
        }

        const rawHtml = marked(
          this.selectedPackage.readmeFile,
          {
            sanitize: false,
            gfm: true,
            headerIds: false,
            // Unfortunately, this isn't working well. See `transformTags` comments, below.
            // baseUrl: this.selectedPackage.assetsPath,
          },
        )
        return {
          html: sanitizeHtml(rawHtml, {
            allowedTags: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'p', 'a', 'ul', 'ol',
              'nl', 'li', 'b', 'i', 'strong', 'em', 'strike', 'code', 'hr', 'br',
              'table', 'thead', 'caption', 'tbody', 'tr', 'th', 'td', 'pre', 'img'],
            allowedAttributes: {
              a: ['href', 'name', 'target'],
              img: ['src', 'align', 'style', 'alt', 'height', 'width'],
            },
            transformTags: {
            // Convert relative to absolute URLs.
            // Inspired by https://github.com/microcosm-cc/bluemonday/blob/f0761eb8ed07c1cc892ef631b00c33463b9b6868/sanitize.go#L321-L349
            // We hoped that the baseUrl parameter would work, but it seems to miss links and
            // images that are inserted in HTML directly (not using markdown syntax). When I used
            // a baseUrl parameter in addition to transformTags, then markdown-based references were
            // rebased twice. Example:
            // https://github.com/tidyverse/dplyr/blame/3e3ade18a3b20bac354b8ba2ae9d29287c260b6a/README.md#L4
              a: uriToAbs('href', this.selectedPackage.assetsPath),
              img: rewriteGithubBlobImageURIs(uriToAbs('src', this.selectedPackage.assetsPath)),
            },
          }),
        }
      } else if (this.selectedPackage.readme) {
        this.$store.dispatch('getPackageReadme', { packageId: this.selectedPackage.id, readmeFileName: this.selectedPackage.readme })
      }
      // Return an empty, valid object to avoid UI errors.
      return { html: '', raw: '' }
    },
  },
  watch: {
    selectedPackage: function (newValue, oldValue) {
      if (newValue.id !== oldValue.id) {
        this.setCurrentTab('overview')
      }
    },
  },
  created: function () {
    const main = this.getDocument.getElementById('main')
    main.scrollTop = 0
    main.scrollLeft = 0
  },
  methods: {
    setCurrentTab: function (tab) {
      this.currentTab = tab
    },
  },
}

</script>
